import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useConfig } from "@/hooks/useConfig";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Plus, Search, Eye } from "lucide-react";
import { fmtDate } from "@/lib/dateFormat";

const STATUS_COLORS = {
  draft: "bg-zinc-100 text-zinc-700", submitted: "bg-blue-100 text-blue-700",
  sent_back: "bg-amber-100 text-amber-700", live: "bg-emerald-100 text-emerald-700",
  suspended: "bg-red-100 text-red-700", rejected: "bg-red-50 text-red-600",
};
const pretty = s => s ? s.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()) : "";

export default function BrandsListPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const perms = user?.permissions || [];
  const isAdmin = (user?.roles || []).some(r => ["Admin","admin"].includes(r));
  const canCreate = isAdmin || perms.includes("brands.create");

  const [brands, setBrands] = useState([]);
  const [counts, setCounts] = useState({});
  const [tab, setTab] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (tab !== "all") params.set("lifecycle", tab);
    if (search) params.set("search", search);
    api.get(`/brands?${params}`).then(r => setBrands(r.data)).catch(e => toast.error(e.response?.data?.detail || "Failed to load")).finally(() => setLoading(false));
    api.get("/brands/counts").then(r => setCounts(r.data)).catch(() => {});
  }, [tab, search]);

  useEffect(() => { load(); }, [load]);

  const tabs = ["all", "draft", "submitted", "sent_back", "live", "suspended", "rejected"];

  return (
    <div className="space-y-4" data-testid="brands-list-page">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-zinc-900">Brands</h1>
        {canCreate && <Button size="sm" onClick={() => navigate("/brands/new")} data-testid="new-brand-btn"><Plus className="w-4 h-4 mr-1" /> New Brand</Button>}
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        {tabs.map(t => (
          <button key={t} onClick={() => setTab(t)} className={`px-3 py-1 rounded-full text-xs font-medium transition ${tab === t ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`} data-testid={`tab-${t}`}>
            {pretty(t)} {counts[t] !== undefined ? `(${counts[t]})` : ""}
          </button>
        ))}
      </div>
      <div className="relative max-w-xs">
        <Search className="absolute left-2 top-2 w-4 h-4 text-zinc-400" />
        <Input className="pl-8 h-8 text-xs" placeholder="Search brands..." value={search} onChange={e => setSearch(e.target.value)} data-testid="brand-search" />
      </div>
      {loading ? <p className="text-xs text-zinc-400">Loading...</p> : brands.length === 0 ? (
        <div className="text-center py-12 text-zinc-400">
          <p className="text-sm">No brands yet.</p>
          {canCreate && <Button size="sm" variant="outline" className="mt-2" onClick={() => navigate("/brands/new")}>Click New Brand to add one</Button>}
        </div>
      ) : (
        <div className="border rounded-md overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-zinc-50 text-zinc-500">
              <tr>
                <th className="px-3 py-2 text-left">Brand</th>
                <th className="px-3 py-2 text-left">Owner</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Products</th>
                <th className="px-3 py-2 text-left">Completeness</th>
                <th className="px-3 py-2 text-left">Do Not Sell</th>
                <th className="px-3 py-2 text-left">Updated</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {brands.map(b => (
                <tr key={b._id} className="hover:bg-zinc-50 cursor-pointer" onClick={() => navigate(`/brands/${b._id}`)} data-testid={`brand-row-${b._id}`}>
                  <td className="px-3 py-2 font-medium text-zinc-900">
                    {b.display_name || b.name}
                    {b.legacy_unreviewed && <Badge variant="outline" className="ml-1 text-[9px] border-amber-300 text-amber-600">Needs review</Badge>}
                  </td>
                  <td className="px-3 py-2 text-zinc-500">{pretty((b.identity || {}).owner_type || b.owner_type || "")}</td>
                  <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${STATUS_COLORS[b.lifecycle_status] || "bg-zinc-100"}`}>{pretty(b.lifecycle_status || "")}</span></td>
                  <td className="px-3 py-2 text-zinc-500">{b.listing_total || 0} / {b.listing_sent || 0} sent</td>
                  <td className="px-3 py-2"><div className="w-16 bg-zinc-200 rounded-full h-1.5"><div className="bg-emerald-500 h-1.5 rounded-full" style={{width: `${b.completeness || 0}%`}} /></div></td>
                  <td className="px-3 py-2">{b.has_restrictions ? <span className="text-red-500 font-medium">Yes</span> : <span className="text-zinc-400">No</span>}</td>
                  <td className="px-3 py-2 text-zinc-400">{fmtDate(b.updated_at)}</td>
                  <td className="px-3 py-2"><Eye className="w-3.5 h-3.5 text-zinc-400" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
